/**
 * Instagram-style fullscreen photo viewer. Horizontal swipe / arrow keys / dots
 * to navigate; tap backdrop or X to close. Caption fixed at the bottom.
 */
import { useEffect, useState } from 'react';
import { resolveMediaUrl } from '@/utils/media';
import type { PortfolioStory } from '@/types/domain';
import { Icon } from '@/components/atoms';
import styles from './ProfileFeed.module.css';

export interface FullscreenViewerProps {
  stories: PortfolioStory[];
  startIndex: number;
  onClose: () => void;
}

export function FullscreenViewer({
  stories,
  startIndex,
  onClose,
}: FullscreenViewerProps): JSX.Element | null {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(stories.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stories.length, onClose]);

  if (!stories.length) return null;
  const current = stories[Math.max(0, Math.min(index, stories.length - 1))];
  if (!current) return null;

  return (
    <div className={styles.viewerRoot} role="dialog" aria-label="Перегляд фото">
      <button
        type="button"
        className={styles.viewerClose}
        onClick={onClose}
        aria-label="Закрити"
      >
        <Icon name="x" size={22} />
      </button>
      {index > 0 && (
        <button
          type="button"
          className={`${styles.viewerNav} ${styles.viewerNavLeft}`}
          onClick={() => setIndex((i) => i - 1)}
          aria-label="Попереднє"
        >
          ‹
        </button>
      )}
      {index < stories.length - 1 && (
        <button
          type="button"
          className={`${styles.viewerNav} ${styles.viewerNavRight}`}
          onClick={() => setIndex((i) => i + 1)}
          aria-label="Наступне"
        >
          ›
        </button>
      )}
      <div className={styles.viewerStage} onClick={onClose}>
        <img
          src={resolveMediaUrl(current.image_url)}
          alt={current.caption || ''}
          className={styles.viewerImage}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      {current.caption ? (
        <p className={styles.viewerCaption}>{current.caption}</p>
      ) : null}
      <div className={styles.viewerDots} aria-hidden>
        {stories.map((_, i) => (
          <span
            key={i}
            className={i === index ? styles.viewerDotActive : styles.viewerDot}
          />
        ))}
      </div>
    </div>
  );
}
